import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fetchReviews, postReview } from "@/lib/api";

function Stars({ value, size = 16 }) {
  return (
    <span className="inline-flex gap-0.5 text-honey">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={size} fill={i < value ? "currentColor" : "none"} className={i < value ? "" : "text-wood-500/30"} />
      ))}
    </span>
  );
}

export default function ReviewsSection({ productSlug, title = "Customer Reviews" }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["reviews", productSlug || "all"], queryFn: () => fetchReviews(productSlug) });
  const reviews = data?.reviews || [];
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await postReview({ ...form, product_slug: productSlug });
      toast[res.verified ? "success" : "message"](res.message);
      qc.invalidateQueries({ queryKey: ["reviews", productSlug || "all"] });
      setShowForm(false);
      setForm({ name: "", email: "", rating: 5, title: "", body: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-24 border-t border-wood-900/10 pt-14" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl text-wood-900 md:text-4xl">{title}</h2>
          {data?.average != null ? (
            <div className="mt-2 flex items-center gap-3">
              <Stars value={Math.round(data.average)} size={18} />
              <span className="text-base text-wood-700">{data.average} · {data.count} verified review{data.count > 1 ? "s" : ""}</span>
            </div>
          ) : (
            <p className="mt-2 text-base text-wood-500">No reviews yet — verified reviews from real customers will appear here.</p>
          )}
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="write-review-toggle" className="rounded-full border border-wood-900/30 px-6 py-3 text-base font-medium text-wood-900 transition-colors hover:bg-wood-900 hover:text-cream">
          Write a review
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="review-form" className="mt-8 grid gap-4 rounded-3xl bg-sand p-6 md:grid-cols-2">
          <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" className="rounded-xl border border-wood-900/20 bg-cream px-4 py-3 text-base outline-none focus:border-honey-dark" />
          <input required type="email" placeholder="Email used at checkout" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="review-email" className="rounded-xl border border-wood-900/20 bg-cream px-4 py-3 text-base outline-none focus:border-honey-dark" />
          <div className="flex items-center gap-3 md:col-span-2">
            <span className="text-base text-wood-700">Your rating:</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} stars`} data-testid={`review-star-${n}`}>
                <Star size={24} className="text-honey" fill={n <= form.rating ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
          <input placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border border-wood-900/20 bg-cream px-4 py-3 text-base outline-none focus:border-honey-dark md:col-span-2" />
          <textarea required rows={4} placeholder="Share your experience…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="review-body" className="resize-none rounded-xl border border-wood-900/20 bg-cream px-4 py-3 text-base outline-none focus:border-honey-dark md:col-span-2" />
          <p className="text-sm text-wood-500 md:col-span-2">To keep reviews honest, we verify each one against a real order using the email you used at checkout.</p>
          <button type="submit" disabled={submitting} data-testid="review-submit" className="rounded-full bg-wood-900 px-8 py-3.5 text-base font-medium text-cream transition-colors hover:bg-honey-dark disabled:opacity-60 md:col-span-2 md:justify-self-start">
            {submitting ? "Submitting…" : "Submit review"}
          </button>
        </form>
      )}

      {reviews.length > 0 && (
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-wood-900/10 bg-white p-6" data-testid="review-item">
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                {r.verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sage/15 px-3 py-1 text-xs font-semibold text-sage">
                    <ShieldCheck size={13} /> Verified buyer
                  </span>
                )}
              </div>
              {r.title && <h3 className="mt-3 font-serif text-xl text-wood-900">{r.title}</h3>}
              <p className="mt-2 text-base leading-relaxed text-wood-700">{r.body}</p>
              <p className="mt-3 text-sm text-wood-500">— {r.name}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
